import {handleActions} from 'redux-actions';
import {Record, List, Set, OrderedSet, Map} from 'immutable';
import moment from 'moment-timezone';

import * as AsyncLinkActions from 'web-app/react/components/async-link-button/actions';
import * as AccountItemConstants from 'web-app/react/entities/account-items/constants';
import BehaviorsCollection from 'web-app/behaviors/behaviors-collection';
import {PaymentsSourceType} from 'web-app/util/graphql/api-types';
import {enumValues} from 'web-app/util/typescript';

import * as Constants from './constants';
import * as Actions from './actions';

const DepositData = Record({
    past: null,
    current: null,
    future: null,
    csv: null,
});

const State = Record({
    targetId: undefined,
    searchItems: List(),
    isLoadingInvoices: true,
    isLoadingPayments: true,
    isLoadingSearchItems: false,
    isLoadingDirectDebitFiles: true,
    isLoadingDeposits: true,
    depositData: null,
    hasMorePayments: true,
    paymentsCSV: '',
    hasMoreInvoices: true,
    showArchivedBillPayers: false,
    invoiceCSV: '',
    directDebitSelection: Constants.DIRECT_DEBITS_NEW,
    selectedConfirmStatus: AccountItemConstants.CONFIRMED_FILTER_ALL,
    selectedDepositStatus: '',
    selectedPaymentMethod: '',
    selectedTransactionStatus: '',
    fromPeriod: null,
    toPeriod: null,
    createdAtFromPeriod: null,
    createdAtToPeriod: null,
    selectedDate: null,
    directDebitFiles: List(),
    directDebitFilesRefreshIn: null,
    directDebitFilesBehaviors: new BehaviorsCollection(),
    directDebitFilesSubmitting: false,
    directCreditFilesSubmitting: false,
    invoicesBehaviors: new BehaviorsCollection(),
    showDirectDebitModal: false,
    showDirectCreditModal: false,
    selection: Constants.PAYMENTS,
    payments: OrderedSet(),
    paymentsCount: 0,
    invoices: OrderedSet(),
    selectedDirectDebitInvoiceIds: Set(),
    allLoadedDirectDebitInvoiceIds: Set(),
    onlyBillPayersWithMissingAddress: false,
    includeCreditNotesAndCancelledInvoicesInBalance: false,
    includeDeletedPayments: false,
    showFilters: false,
    selectedBillPayersIdsFilter: '',
    fetchingBillPayersForFilter: false,
    billPayersForFilter: List(),
    billPayersFilterSearchTerm: null,
    selectedPaymentTypesFilter: enumValues(PaymentsSourceType).reduce((map, type) => map.set(type, false), Map()),
    selectedInvoices: Map(),
    showBulkCancelInvoiceModal: false,
    generateExportError: false,
});

export default handleActions(
    {
        [Actions.periodChanged.type]: (state, {payload}) =>
            state.merge({
                fromPeriod: payload.from,
                toPeriod: payload.to,
            }),
        [Actions.createdAtPeriodChanged.type]: (state, {payload}) =>
            state.merge({
                createdAtFromPeriod: payload.from,
                createdAtToPeriod: payload.to,
            }),
        [Actions.showArchivedBillPayersChanged.type]: (state, {payload}) =>
            state.merge({
                showArchivedBillPayers: payload.show,
            }),
        [Actions.makeDirectDebitRun.type]: state =>
            state.merge({
                directDebitFilesSubmitting: true,
            }),
        [Actions.makeDirectDebitRunSuccess.type]: state =>
            state.merge({
                showDirectDebitModal: false,
                directDebitFilesSubmitting: false,
            }),
        [Actions.makeDirectDebitRunFailed.type]: state =>
            state.merge({
                showDirectDebitModal: false,
                directDebitFilesSubmitting: false,
            }),
        [Actions.showDirectDebitModal.type]: state =>
            state.merge({
                showDirectDebitModal: true,
            }),
        [Actions.hideDirectDebitModal.type]: state =>
            state.merge({
                showDirectDebitModal: false,
            }),
        [Actions.makeDirectCreditRun.type]: state =>
            state.merge({
                directCreditFilesSubmitting: true,
            }),
        [Actions.makeDirectCreditRunSuccess.type]: state =>
            state.merge({
                showDirectCreditModal: false,
                directCreditFilesSubmitting: false,
            }),
        [Actions.makeDirectCreditRunFailed.type]: state =>
            state.merge({
                showDirectCreditModal: false,
                directCreditFilesSubmitting: false,
            }),
        [Actions.showDirectCreditModal.type]: state =>
            state.merge({
                showDirectCreditModal: true,
            }),
        [Actions.hideDirectCreditModal.type]: state =>
            state.merge({
                showDirectCreditModal: false,
            }),

        [Actions.fetchDeposits.type]: state =>
            state.merge({
                isLoadingDeposits: true,
            }),
        [Actions.fetchDepositsSuccess.type]: (state, {payload}) =>
            state.merge({
                isLoadingDeposits: false,
                depositData: new DepositData({
                    past: payload.past,
                    current: payload.current,
                    future: payload.future,
                    csv: payload.csv,
                }),
            }),
        [Actions.fetchDepositsFailed.type]: state =>
            state.merge({
                isLoadingDeposits: false,
            }),
        [Actions.fetchDirectDebitFiles.type]: state =>
            state.merge({
                isLoadingDirectDebitFiles: true,
            }),
        [Actions.fetchDirectDebitFilesSuccess.type]: (state, {payload}) =>
            state.merge({
                isLoadingDirectDebitFiles: false,
                directDebitFiles: payload.response.files,
                directDebitFilesBehaviors: payload.response.behaviors,
                directDebitFilesRefreshIn: payload.response.refreshIn,
            }),
        [Actions.fetchDirectDebitFilesFailed.type]: state =>
            state.merge({
                isLoadingDirectDebitFiles: false,
            }),
        [Actions.showingTargetId.type]: (state, {payload}) =>
            new State({
                selectedDate: moment().format('YYYY-MM-DD'),
                targetId: payload.targetId,
                showArchivedBillPayers: false,
                selection: Constants.PAYMENTS,
            }),
        [Actions.fetchPayments.type]: (state, {payload}) =>
            state.merge({
                isLoadingPayments: true,
                payments: payload.offset === 0 ? OrderedSet() : state.payments,
            }),
        [Actions.fetchPaymentsFailed.type]: state => state.merge({isLoadingPayments: false}),
        [Actions.fetchPaymentsSuccess.type]: (state, {payload}) => {
            const payments = payload.payments;
            if (payments.length === 0 && payload.offset !== 0) {
                return state.merge({
                    isLoadingPayments: false,
                    paymentsCount: payload.total,
                });
            } else {
                const paymentIds = payments.map(p => p.id);

                return state.merge({
                    hasMorePayments: payload.payments.length === Constants.PAYMENT_AND_INVOICES_LIMIT,
                    paymentsCSV: payload.csv,
                    isLoadingPayments: false,
                    payments: payload.offset !== 0 ? state.payments.concat(paymentIds) : OrderedSet(paymentIds),
                    paymentsCount: payload.total,
                });
            }
        },

        [Actions.dateChanged.type]: (state, {payload}) =>
            state.merge({
                selectedDate: payload.selectedDate,
            }),

        [Actions.fetchSearchItems.type]: state =>
            state.merge({
                isLoadingSearchItems: true,
            }),

        [Actions.fetchSearchItemsFailed.type]: state =>
            state.merge({
                isLoadingSearchItems: false,
            }),

        [Actions.fetchSearchItemsSuccess.type]: (state, {payload}) => {
            return state.merge({
                searchItems: payload.searchItems,
                isLoadingSearchItems: false,
            });
        },

        [Actions.toggleDirectDebitInvoiceCheck.type]: (state, {payload}) => {
            const invoices = state.selectedDirectDebitInvoiceIds;
            return state.merge({
                selectedDirectDebitInvoiceIds: invoices.contains(payload.invoiceId)
                    ? invoices.delete(payload.invoiceId)
                    : invoices.add(payload.invoiceId),
            });
        },

        [Actions.fetchInvoices.type]: (state, {payload}) =>
            state.merge({
                isLoadingInvoices: true,
                invoices: payload.offset === 0 ? OrderedSet() : state.invoices,
            }),
        [Actions.fetchInvoicesFailed.type]: state => state.merge({isLoadingInvoices: false}),
        [Actions.fetchInvoicesSuccess.type]: (state, {payload}) => {
            const invoices = payload.invoices;
            if (invoices.length === 0 && payload.offset !== 0) {
                return state.merge({
                    isLoadingInvoices: false,
                });
            } else {
                const invoiceIds = invoices.map(i => i.id);

                return state.merge({
                    hasMoreInvoices: payload.invoices.length === Constants.PAYMENT_AND_INVOICES_LIMIT,
                    invoiceCSV: payload.csv,
                    invoicesBehaviors: payload.behaviors,
                    isLoadingInvoices: false,
                    selectedDirectDebitInvoiceIds: state.selectedDirectDebitInvoiceIds.concat(
                        payload.invoices.filter(item => item.directDebit).map(item => item.id),
                    ),
                    allLoadedDirectDebitInvoiceIds: state.allLoadedDirectDebitInvoiceIds.concat(
                        payload.invoices.filter(item => item.directDebit).map(item => item.id),
                    ),
                    invoices: payload.offset !== 0 ? state.invoices.concat(invoiceIds) : OrderedSet(invoiceIds),
                    selectedInvoices: payload.offset === 0 ? Map() : state.selectedInvoices,
                });
            }
        },

        [Actions.selectionChanged.type]: (state, {payload}) =>
            state.merge({
                selection: payload.selection,
                showArchivedBillPayers: false,
                directDebitSelection: Constants.DIRECT_DEBITS_NEW,
                selectedInvoices: Map(),
            }),

        [Actions.confirmedStatusChanged.type]: (state, {payload}) =>
            state.merge({
                selectedConfirmStatus: payload.status,
            }),

        [Actions.paymentMethodChanged.type]: (state, {payload}) =>
            state.merge({
                selectedPaymentMethod: payload.method,
            }),

        [Actions.depositStatusChanged.type]: (state, {payload}) =>
            state.merge({
                selectedDepositStatus: payload.method,
            }),

        [Actions.directDebitSelectionChanged.type]: (state, {payload}) =>
            state.merge({
                directDebitSelection: payload.selection,
                selectedDirectDebitInvoiceIds: state.allLoadedDirectDebitInvoiceIds,
            }),

        [Actions.transactionStatusChanged.type]: (state, {payload}) =>
            state.merge({
                selectedTransactionStatus: payload.transactionStatus,
            }),

        [Actions.onlyBillPayersWithMissingAddressChanged.type]: (state, {payload}) =>
            state.merge({
                onlyBillPayersWithMissingAddress: payload.onlyBillPayersWithMissingAddress,
            }),

        [Actions.includeCreditNotesAndCancelledInvoicesInBalanceChanged.type]: (state, {payload}) =>
            state.merge({
                includeCreditNotesAndCancelledInvoicesInBalance:
                    payload.includeCreditNotesAndCancelledInvoicesInBalance,
            }),

        [Actions.includeDeletedPaymentsChanged.type]: (state, {payload}) =>
            state.merge({
                includeDeletedPayments: payload.includeDeletedPayments,
            }),

        [Actions.toggleFilters.type]: state => {
            return state.merge({
                showFilters: !state.showFilters,
            });
        },
        [Actions.fetchBillPayersForFilter.type]: (state, {payload}) => {
            return state.merge({
                fetchingBillPayersForFilter: true,
                billPayersFilterSearchTerm: payload.term,
            });
        },
        [Actions.fetchBillPayersForFilterSuccess.type]: (state, {payload}) => {
            return state.merge({
                fetchingBillPayersForFilter: false,
                billPayersForFilter: state.billPayersForFilter
                    .concat(payload.searchItems)
                    .groupBy(result => result.id)
                    .map(result => result.first())
                    .toList()
                    .sortBy(result => result.title),
            });
        },
        [Actions.fetchBillPayersForFilterFailed.type]: state => {
            return state.merge({
                fetchingBillPayersForFilter: false,
            });
        },
        [Actions.fetchBillPayersForFilterAborted.type]: state => {
            return state.merge({
                fetchingBillPayersForFilter: false,
            });
        },
        [Actions.selectBillPayers.type]: (state, {payload}) => {
            return state.merge({
                selectedBillPayersIdsFilter: payload.selectedBillPayers,
            });
        },
        [Actions.setPaymentSourceTypeFilterValues.type]: (state, {payload}) => {
            return state.merge({
                selectedPaymentTypesFilter: payload.newPaymentSourceTypeFilterValues,
            });
        },
        [Actions.setInvoiceSelected.type]: (state, {payload}) => {
            return state.merge({
                selectedInvoices: state.selectedInvoices.set(payload.invoiceId, payload.isSelected),
            });
        },
        [Actions.resetCancelInvoice.type]: state => {
            return state.merge({
                selectedInvoices: Map(),
            });
        },
        [Actions.setShowBulkCancelInvoiceModal.type]: (state, {payload}) => {
            return state.merge({
                showBulkCancelInvoiceModal: payload.show,
            });
        },
        [Actions.bulkCancelInvoicesSuccess.type]: state => {
            return state.merge({
                showBulkCancelInvoiceModal: false,
            });
        },
        [Actions.selectAllInvoices.type]: (state, {payload}) => {
            return state.merge({
                selectedInvoices: payload.selectedInvoices,
            });
        },
        [AsyncLinkActions.generateAsyncLinkFailed.type]: state => {
            return state.merge({
                generateExportError: true,
            });
        },
        [AsyncLinkActions.generateAsyncLink.type]: state => {
            return state.merge({
                generateExportError: false,
            });
        },
        [AsyncLinkActions.generateAsyncLinkSuccess.type]: state => {
            return state.merge({
                generateExportError: false,
            });
        },
    },
    new State(),
);
